import React, { useContext, useEffect, useMemo, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  Badge,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import ViewTimelineOutlinedIcon from "@mui/icons-material/ViewTimelineOutlined";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
// import MoreTimeOutlinedIcon from "@mui/icons-material/MoreTimeOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import { SidebarContext } from "../../Sidebar/SidebarProvider";
import { pageFieldSx } from "../../../styles/pageModernStyles";
import { getUserById } from "../../../api/User/userApi";
import { fetchTimeTrackingEmployees } from "../../../api/TimeTracking/timeTrackingApi";
import { mapUserToPermissionState } from "../../../utils/userPermissionsUtils";
import {
  ttDesktopHeaderSx,
  ttPageOuterSx,
  ttTabsDesktopSx,
  ttTabsSx,
  ttUnifiedPanelSx,
  ttUnifiedScrollSx,
} from "./timeTrackingStyles";
import ClockTab from "./ClockTab";
import HistoryTab from "./HistoryTab";
import DailyBoardTab from "./DailyBoardTab";
import TimeOffTab from "./TimeOffTab";
// import OvertimeTab from "./OvertimeTab";
import CorrectionRequestsTab from "./CorrectionRequestsTab";
import CalendarTab from "./CalendarTab";
import { useTranslation } from "react-i18next";
import { usePendingTimeTrackingCount } from "../../../hooks/usePendingTimeTrackingCount";

const TIME_TRACKING_TAB_DEFS = [
  {
    id: "clock",
    icon: LoginOutlinedIcon,
    label: "Fichaje",
    mobileLabel: "Fichar",
  },
  {
    id: "daily",
    icon: ViewTimelineOutlinedIcon,
    label: "Vista día",
    mobileLabel: "Día",
    rrhhOnly: true,
  },
  {
    id: "history",
    icon: HistoryOutlinedIcon,
    label: "Historial",
    mobileLabel: "Historial",
  },
  {
    id: "timeoff",
    icon: BeachAccessOutlinedIcon,
    label: "Solicitud Vacaciones y permisos",
    mobileLabel: "Vacaciones",
  },
  {
    id: "calendar",
    icon: CalendarMonthOutlinedIcon,
    label: "Calendario",
    mobileLabel: "Calendario",
  },
  {
    id: "corrections",
    icon: EditNoteOutlinedIcon,
    label: "Solicitudes Fichaje",
    mobileLabel: "Correcciones",
  },
];

const TimeTrackingPage = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { contentMargin } = useContext(SidebarContext);
  const navigate = useNavigate();
  const [activeTabId, setActiveTabId] = useState("clock");
  const [username, setUsername] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [canFichaje, setCanFichaje] = useState(false);
  const [canRrhh, setCanRrhh] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const pendingHorario = usePendingTimeTrackingCount();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (decoded.exp < currentTime) {
        localStorage.removeItem("token");
        navigate("/login");
        return;
      }
      setUsername(decoded.username || "Usuario");
      setCurrentUserId(decoded.userId);

      const loadPerms = async () => {
        const user = await getUserById(decoded.userId);
        const perms = mapUserToPermissionState(user);
        const rrhh = !!perms.Permiso_ControlHorario_RRHH;
        const fichaje = !!perms.Permiso_Fichaje;
        if (!fichaje && !rrhh) {
          navigate("/");
          return;
        }
        setCanFichaje(fichaje);
        setCanRrhh(rrhh);
        if (rrhh) {
          const list = await fetchTimeTrackingEmployees();
          setEmployees(list);
        } else if (fichaje) {
          setEmployees([
            { id: decoded.userId, username: decoded.username || "Yo" },
          ]);
        }
      };
      loadPerms().catch(console.error);
    } catch (err) {
      console.error(err);
      navigate("/login");
    }
  }, [navigate]);

  const effectiveUserId = selectedUserId ? Number(selectedUserId) : null;
  const accent = theme.palette.primary.main;

  const visibleTabs = useMemo(
    () =>
      TIME_TRACKING_TAB_DEFS.filter((tabDef) => !tabDef.rrhhOnly || canRrhh),
    [canRrhh],
  );

  const activeTabIndex = Math.max(
    0,
    visibleTabs.findIndex((tabDef) => tabDef.id === activeTabId),
  );

  const activeTabMeta = visibleTabs[activeTabIndex];

  useEffect(() => {
    if (!visibleTabs.some((tabDef) => tabDef.id === activeTabId)) {
      setActiveTabId(visibleTabs[0]?.id || "clock");
    }
  }, [visibleTabs, activeTabId]);

  const roleChip = () => {
    if (canRrhh && canFichaje) {
      return (
        <Chip
          label="RRHH + empleado"
          size="small"
          sx={{
            height: 20,
            fontWeight: 700,
            fontSize: "0.62rem",
            bgcolor: alpha(theme.palette.info.main, 0.1),
            color: theme.palette.info.dark,
          }}
        />
      );
    }
    if (canRrhh) {
      return (
        <Chip
          label="Vista RRHH"
          size="small"
          sx={{
            height: 20,
            fontWeight: 700,
            fontSize: "0.62rem",
            bgcolor: alpha(accent, 0.12),
            color: theme.palette.primary.dark,
          }}
        />
      );
    }
    return (
      <Chip
        label="Mis registros"
        size="small"
        variant="outlined"
        sx={{ height: 22, fontWeight: 700, fontSize: "0.68rem" }}
      />
    );
  };

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        height: "100dvh",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minWidth: 0,
        pt: { xs: 1.25, md: 2 },
        px: { xs: 1.25, md: 3 },
        pb: { xs: 1.25, md: 2 },
        overflow: "hidden",
        ...contentMargin,
        ...ttPageOuterSx(theme),
      }}
    >
      <Paper elevation={0} sx={ttUnifiedPanelSx(theme)}>
        <Box sx={ttDesktopHeaderSx(theme, accent)}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 0.75, sm: 1.25 }}
            justifyContent="space-between"
            alignItems={{ sm: "center" }}
            sx={{ pl: { md: 1.25 } }}
          >
            <Stack
              direction="row"
              spacing={{ xs: 1, md: 1.5 }}
              alignItems="center"
              sx={{ minWidth: 0, flex: 1 }}
            >
              <Box
                sx={{
                  width: { xs: 32, sm: 36, md: 40 },
                  height: { xs: 32, sm: 36, md: 40 },
                  borderRadius: { xs: 1.5, md: 2 },
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(accent, 0.14),
                  color: theme.palette.primary.dark,
                  border: "1px solid",
                  borderColor: alpha(accent, 0.22),
                  boxShadow: {
                    md: `0 4px 14px ${alpha(accent, 0.15)}`,
                  },
                }}
              >
                <AccessTimeOutlinedIcon
                  sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.15,
                    fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.15rem" },
                  }}
                >
                  {t("sidebar.controlHorario", {
                    defaultValue: "Control horario",
                  })}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.5}
                  useFlexGap
                  flexWrap="wrap"
                  alignItems="center"
                  sx={{ mt: 0.35, rowGap: 0.25 }}
                >
                  <Chip
                    label={username}
                    size="small"
                    sx={{
                      height: 20,
                      fontWeight: 700,
                      fontSize: "0.62rem",
                      bgcolor: alpha(theme.palette.text.primary, 0.06),
                    }}
                  />
                  {roleChip()}
                  {canRrhh && !isMobile ? (
                    <Chip
                      label={t("timeTracking.employeeCount", {
                        defaultValue: "{{count}} empleados",
                        count: employees.length,
                      })}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontWeight: 700, fontSize: "0.62rem" }}
                    />
                  ) : null}
                  {isMobile ? (
                    <Chip
                      size="small"
                      label={
                        activeTabMeta?.mobileLabel || activeTabMeta?.label || ""
                      }
                      color="primary"
                      variant="outlined"
                      sx={{ height: 20, fontWeight: 800, fontSize: "0.6rem" }}
                    />
                  ) : null}
                </Stack>
              </Box>
            </Stack>

            {canRrhh ? (
              <TextField
                select
                size="small"
                label={t("timeTracking.employee", { defaultValue: "Empleado" })}
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                sx={{
                  ...pageFieldSx(theme),
                  width: { xs: "100%", sm: 220, md: 240 },
                  flexShrink: 0,
                  "& .MuiInputBase-root": { height: 36 },
                }}
              >
                <MenuItem value="">
                  {t("timeTracking.allEmployees", {
                    defaultValue: "Todos los empleados",
                  })}
                </MenuItem>
                {employees.map((e) => (
                  <MenuItem key={e.id} value={String(e.id)}>
                    {e.username}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
          </Stack>
        </Box>

        <Tabs
          value={activeTabIndex}
          onChange={(_, v) => setActiveTabId(visibleTabs[v]?.id || "clock")}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={isMobile ? ttTabsSx(theme) : ttTabsDesktopSx(theme)}
        >
          {visibleTabs.map(({ id, icon: Icon, label, mobileLabel }) => {
            const tabPending =
              id === "timeoff"
                ? pendingHorario.timeOff
                : id === "corrections"
                  ? pendingHorario.corrections
                  : 0;
            return (
              <Tab
                key={id}
                icon={
                  <Badge
                    badgeContent={tabPending}
                    invisible={!canRrhh || tabPending === 0}
                    max={99}
                    sx={{
                      "& .MuiBadge-badge": {
                        backgroundColor: "#e53935",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "0.62rem",
                        minWidth: 16,
                        height: 16,
                        top: 2,
                        right: -4,
                      },
                    }}
                  >
                    <Icon />
                  </Badge>
                }
                iconPosition="start"
                label={isMobile ? mobileLabel : label}
              />
            );
          })}
        </Tabs>

        <Box sx={ttUnifiedScrollSx(theme)}>
          {activeTabId === "clock" && (
            <ClockTab
              selectedUserId={effectiveUserId}
              currentUserId={currentUserId}
              canRrhh={canRrhh}
              canFichaje={canFichaje}
            />
          )}
          {activeTabId === "daily" && (
            <DailyBoardTab
              selectedUserId={effectiveUserId}
              canRrhh={canRrhh}
              employees={employees}
            />
          )}
          {activeTabId === "history" && (
            <HistoryTab
              selectedUserId={effectiveUserId}
              canRrhh={canRrhh}
              canFichaje={canFichaje}
              employees={employees}
              onGoToCorrections={() => setActiveTabId("corrections")}
            />
          )}
          {activeTabId === "timeoff" && (
            <TimeOffTab
              selectedUserId={effectiveUserId}
              canRrhh={canRrhh}
              canFichaje={canFichaje}
              employees={employees}
            />
          )}
          {activeTabId === "calendar" && (
            <CalendarTab
              selectedUserId={effectiveUserId}
              canRrhh={canRrhh}
              canFichaje={canFichaje}
              employees={employees}
            />
          )}
          {/* Horas extra — oculto de momento
          {activeTabId === "overtime" && (
            <OvertimeTab
              selectedUserId={effectiveUserId}
              canRrhh={canRrhh}
              canFichaje={canFichaje}
            />
          )}
          */}
          {activeTabId === "corrections" && (
            <CorrectionRequestsTab
              selectedUserId={effectiveUserId}
              canRrhh={canRrhh}
              canFichaje={canFichaje}
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default TimeTrackingPage;
